import { useEffect, useState } from "react";
import { isAddress, type Address } from "viem";
import { generatePrivateKey, privateKeyToAccount } from "viem/accounts";
import { config } from "../config";
import { marketAbi } from "../abi";
import { l2Public } from "../lib/chain";
import { approveIfNeeded, writeL2 } from "../lib/tx";
import { fmtToken, parseToken } from "../lib/format";
import { useWallet } from "../wallet";
import { ActionButton, Card, Field, Notice, Stat } from "../components/ui";

/**
 * API access — the crypto-native "generate an API key" flow. There are no shared secrets:
 * payment authorization is the per-request receipt signature, so the credential is a SESSION KEY —
 * a fresh keypair generated in this browser, whose spending balance is its entire budget.
 * Funding uses the market's depositFor, so the key itself never needs gas.
 */

interface SessionKey {
  privateKey: `0x${string}`;
  address: Address;
}

function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn ghost sm"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? "Copied ✓" : label}
    </button>
  );
}

function CodeBlock({ text }: { text: string }) {
  return (
    <div className="copywrap">
      <pre className="codeblock" style={{ margin: 0 }}>{text}</pre>
      <div className="copybtn">
        <CopyButton text={text} />
      </div>
    </div>
  );
}

export function Api() {
  const { account } = useWallet();
  const [key, setKey] = useState<SessionKey | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [beneficiary, setBeneficiary] = useState("");
  const [amount, setAmount] = useState("1");
  const [balance, setBalance] = useState<bigint | null>(null);
  const [nonce, setNonce] = useState(0);

  const validBeneficiary = isAddress(beneficiary);

  function generate() {
    const privateKey = generatePrivateKey();
    const address = privateKeyToAccount(privateKey).address;
    setKey({ privateKey, address });
    setRevealed(false);
    setBeneficiary(address);
  }

  useEffect(() => {
    if (!validBeneficiary) {
      setBalance(null);
      return;
    }
    l2Public
      .readContract({
        address: config.l2.contracts.market,
        abi: marketAbi,
        functionName: "balances",
        args: [beneficiary as Address],
      })
      .then((b) => setBalance(b as bigint))
      .catch(() => setBalance(null));
  }, [beneficiary, validBeneficiary, nonce]);

  const envBlock = [
    `export BASEDAI_PRIVATE_KEY=${key ? key.privateKey : "0x<your session key>"}`,
    `export BASEDAI_RPC_URL=${config.l2.rpcUrl}`,
    `export BASEDAI_CHAIN_ID=${config.l2.chainId}`,
    `export BASEDAI_BASED=${config.l2.contracts.based}`,
    `export BASEDAI_SUBNET_REGISTRY=${config.l2.contracts.subnetRegistry}`,
    `export BASEDAI_MARKET=${config.l2.contracts.market}`,
    `export BASEDAI_GATEWAY_URL=${config.gatewayUrl}`,
    `export BASEDAI_BRAIN_ID=7`,
    `export BASEDAI_BUDGET=0.05`,
  ].join("\n");

  const sidecarBlock = [
    `pip install basedai-client`,
    `basedai-sidecar   # OpenAI-compatible endpoint on http://127.0.0.1:8402/v1`,
    ``,
    `# then from any OpenAI client or agent framework:`,
    `from openai import OpenAI`,
    `ai = OpenAI(base_url="http://127.0.0.1:8402/v1", api_key="basedai")  # key is ignored`,
    `r = ai.chat.completions.create(model="brain-7",`,
    `                               messages=[{"role": "user", "content": "hello"}])`,
    `print(r.choices[0].message.content)`,
  ].join("\n");

  const sdkBlock = [
    `from basedai_client import BasedClient, InferenceRequest`,
    `from basedai_client.sidecar import client_config_from_env`,
    ``,
    `client = BasedClient(client_config_from_env())  # reads the env block above`,
    `res = client.infer(InferenceRequest(brain_id=7, prompt="hello",`,
    `                                    budget=50_000_000_000_000_000))  # 0.05 BASED cap`,
    `print(res.text, res.amount)`,
  ].join("\n");

  return (
    <>
      <div className="section-title">API access — use a Brain from your own agent</div>
      <Notice>
        BasedAI has no shared-secret API keys: payment is authorized by a <strong>signature on every
        receipt</strong>, so the credential is a <strong>session key</strong> — a throwaway wallet whose
        spending balance is its entire budget. Leak it and the worst case is that balance; revoke it by
        withdrawing. Inference itself needs <strong>no ETH</strong>: the key only signs off-chain, and miners
        pay the settlement gas.
      </Notice>

      <div className="grid cols-2" style={{ marginTop: 18 }}>
        <div className="stack">
          <Card title="1 · Generate a session key" sub="Created in your browser. It never leaves this page.">
            <button className="btn" onClick={generate}>
              {key ? "Generate a new key" : "Generate key"}
            </button>
            {key && (
              <div className="stack" style={{ marginTop: 14 }}>
                <Stat label="Address" value={<span className="mono" style={{ fontSize: 13 }}>{key.address}</span>} />
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>Private key</label>
                  <div className="row">
                    <span className="mono" style={{ fontSize: 12.5, wordBreak: "break-all" }}>
                      {revealed ? key.privateKey : "0x" + "•".repeat(64)}
                    </span>
                  </div>
                  <div className="row" style={{ marginTop: 8 }}>
                    <button className="btn ghost sm" onClick={() => setRevealed((r) => !r)}>
                      {revealed ? "Hide" : "Reveal"}
                    </button>
                    <CopyButton text={key.privateKey} label="Copy key" />
                  </div>
                </div>
                <Notice kind="warn">
                  Save this key now — it exists only in this browser tab and cannot be recovered after you
                  leave. Anyone holding it can spend the key&apos;s funded balance (and nothing else).
                </Notice>
              </div>
            )}
          </Card>

          <Card title="2 · Fund its spending balance" sub="Your wallet pays; the session key spends.">
            <Field
              label="Session key address"
              hint={beneficiary && !validBeneficiary ? "Not a valid address." : "Auto-filled from step 1, or paste an existing agent key to top it up."}
            >
              <input
                className="input mono"
                value={beneficiary}
                onChange={(e) => setBeneficiary(e.target.value)}
                placeholder="0x…"
              />
            </Field>
            <Field label="Amount ($basedAI)">
              <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </Field>
            <Stat
              label="Session key spending balance"
              value={balance !== null ? fmtToken(balance) : "—"}
              unit="BASED"
            />
            <div style={{ marginTop: 10 }}>
              <ActionButton
                disabled={!account || !validBeneficiary}
                onRun={async () => {
                  if (!account) throw new Error("Connect your wallet first.");
                  const amt = parseToken(amount);
                  await approveIfNeeded(account, config.l2.contracts.based, config.l2.contracts.market, amt);
                  return writeL2(account, {
                    address: config.l2.contracts.market,
                    abi: marketAbi,
                    functionName: "depositFor",
                    args: [beneficiary as Address, amt],
                  });
                }}
                onDone={() => setNonce((n) => n + 1)}
              >
                Approve &amp; fund
              </ActionButton>
            </div>
            {!account && (
              <div style={{ marginTop: 10 }}>
                <Notice>Connect your wallet (top right) to fund the key.</Notice>
              </div>
            )}
          </Card>
        </div>

        <div className="stack">
          <Card title="3 · Connect your agent" sub="Environment for the SDKs and the OpenAI-compatible sidecar.">
            <CodeBlock text={envBlock} />
            <div className="hint" style={{ marginTop: 8 }}>
              {key
                ? "Includes the key generated in step 1 — handle like cash."
                : "Generate a key in step 1 to fill in BASEDAI_PRIVATE_KEY."}
            </div>
          </Card>

          <Card title="Option A — OpenAI-compatible sidecar" sub="Drop-in for any agent framework; runs locally so signing stays local.">
            <CodeBlock text={sidecarBlock} />
          </Card>

          <Card title="Option B — direct SDK" sub="Full control over budgets and Brains (Python; TypeScript equivalent in client/typescript).">
            <CodeBlock text={sdkBlock} />
          </Card>

          <Notice>
            Details and security notes in the{" "}
            <a href="#/docs/agent-access">agent access guide</a>.
          </Notice>
        </div>
      </div>
    </>
  );
}
